/* SplashIntro — صفحة البداية الانفتاحية قبل أي شيء آخر في التطبيق.
   تظهر مرة واحدة لكل جلسة متصفح (sessionStorage)، مع زر تخطّي ضمني (نقر في أي
   مكان) وإغلاق تلقائي بعد ~4.5 ثوانٍ. التصميم ١:١ مطابق للـHTML المعتمد من المستخدم
   (مع تبديل scale.png إلى أيقونة LEXIQ الموجودة في /public). */
import { useEffect, useState } from "react";
import { useUser } from "../context/UserAuthContext";

const SHOWN_KEY = "lexiq.splash.shown.v1";
const AUTO_DISMISS_MS = 5000;     // first launch — give the brand a moment
const FAST_DISMISS_MS = 1400;     // subsequent launches — snappier
const FADE_MS = 600;

/** Bubble param generator. Lives outside React so the purity rules don't
    flag the Math.random() calls. Called once via lazy useState. */
function generateBubbles(n = 40) {
  const arr = [];
  for (let i = 0; i < n; i++) {
    const left = Math.min(99, Math.max(0, (i / n) * 100 + (Math.random() * 4.4 - 2.2)));
    const size = (2 + Math.random() * 4.5).toFixed(1);
    const dur = (10 + Math.random() * 6).toFixed(1);
    // NEGATIVE delays — each bubble starts as if it was already
    // mid-trajectory when the splash mounted, so the screen is alive
    // from frame zero instead of slowly filling up.
    const delay = (-Math.random() * Number(dur)).toFixed(1);
    const drift = Math.round(Math.random() * 52 - 26);
    const op = (0.16 + Math.random() * 0.26).toFixed(2);
    arr.push({ left, size, dur, delay, drift, op });
  }
  return arr;
}

export default function SplashIntro({ onFinish, fast = false }) {
  const [closing, setClosing] = useState(false);
  const [closed, setClosed] = useState(false);
  const [bubbles] = useState(() => generateBubbles());

  const close = () => {
    if (closed) return;
    setClosed(true);
    setClosing(true);
    setTimeout(() => { if (onFinish) onFinish(); }, FADE_MS);
  };

  useEffect(() => {
    // While the splash is up, force the iOS notch / Android status-bar to
    // match the splash background. Restore the previous value on close so
    // each page's own theme-color logic keeps working.
    const meta = typeof document !== "undefined"
      ? document.querySelector('meta[name="theme-color"]:not([media])')
      : null;
    const prev = meta ? meta.getAttribute("content") : null;
    if (meta) meta.setAttribute("content", "#FAFAFA");

    const t = setTimeout(close, fast ? FAST_DISMISS_MS : AUTO_DISMISS_MS);
    return () => {
      clearTimeout(t);
      if (meta && prev != null) meta.setAttribute("content", prev);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      data-testid="splash-intro"
      onClick={close}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "#FAFAFA",
        display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden", direction: "rtl",
        fontFamily: "'Amiri', serif",
        padding: "calc(env(safe-area-inset-top) + 16px) 20px calc(env(safe-area-inset-bottom) + 16px) 20px",
        opacity: closing ? 0 : 1,
        transition: `opacity ${FADE_MS}ms ease-out`,
        pointerEvents: closing ? "none" : "auto",
        cursor: "pointer",
      }}
    >
      {/* Bubbles */}
      <div
        aria-hidden="true"
        style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 1 }}
      >
        {bubbles.map((b, i) => (
          <span
            key={i}
            className="lexiq-splash-bub"
            style={{
              left: `${b.left}%`,
              width: `${b.size}px`,
              height: `${b.size}px`,
              "--dur": `${b.dur}s`,
              "--delay": `${b.delay}s`,
              "--drift": `${b.drift}px`,
              "--op": b.op,
            }}
          />
        ))}
      </div>

      {/* Stage — uses a 3-row grid so the LEXIQ wordmark sits at the EXACT
          vertical center of the viewport. Icon hugs the bottom of the upper
          spacer, taglines stack at the top of the lower spacer. */}
      <div
        style={{
          position: "relative", zIndex: 3,
          display: "grid", gridTemplateRows: "1fr auto 1fr",
          alignItems: "center", justifyItems: "center",
          textAlign: "center",
          width: "100%", maxWidth: 480, height: "100%",
        }}
      >
        {/* Icon — sits at the bottom of the upper row, just above LEXIQ */}
        <div className="lexiq-splash-icon-wrap" style={{ alignSelf: "end" }} aria-hidden="true">
          <img
            src="/scale.png"
            alt="LEXIQ"
            className="lexiq-splash-icon"
            draggable="false"
          />
        </div>

        {/* Logo wordmark — true vertical centre */}
        <div className="lexiq-splash-logo" dir="ltr">
          <span className="lexiq-splash-lex">LEX</span>
          <span className="lexiq-splash-iq">IQ</span>
        </div>

        {/* Taglines — hug the top of the lower row, just under LEXIQ */}
        <div style={{ alignSelf: "start", width: "100%" }}>
          <div className="lexiq-splash-t1">
            العَدْلُ أَساسُ المُلْكِ، وَالقَضاءُ حِصْنُها الحَصينُ
          </div>
          <div className="lexiq-splash-t2">
            ذَكاءٌ قانونيٌّ بِمَنطِقِ <b>القاضي</b> وَدِقَّةِ <b>المُحامي</b>
          </div>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Amiri:wght@400;700&family=Poppins:wght@500;600;700;800&display=swap');

        .lexiq-splash-bub {
          position: absolute;
          bottom: -20px;
          border-radius: 50%;
          background: radial-gradient(circle at 35% 30%,
            rgba(255,255,255,.7),
            rgba(16,185,129,.32) 60%,
            rgba(16,185,129,0) 100%);
          opacity: 0;
          animation: lexiqSplashRise var(--dur) ease-in-out var(--delay) infinite;
          will-change: transform, opacity;
        }
        @keyframes lexiqSplashRise {
          0%   { transform: translateY(0) translateX(0) scale(.85); opacity: 0; }
          10%  { opacity: var(--op); }
          50%  { transform: translateY(-55vh) translateX(calc(var(--drift)*.5)) scale(1); }
          90%  { opacity: var(--op); }
          100% { transform: translateY(-112vh) translateX(var(--drift)) scale(.9); opacity: 0; }
        }

        .lexiq-splash-icon-wrap {
          position: relative;
          width: 180px; height: 180px;
          margin-bottom: 4px;
          flex: none;
          opacity: 0;
          transform: translateY(24px) scale(.88);
          /* smoother, longer entrance — feels intentional, not snappy */
          animation: lexiqSplashPop 1.4s .15s cubic-bezier(.22,1,.36,1) forwards;
        }
        .lexiq-splash-icon {
          width: 100%; height: 100%;
          object-fit: contain;
          display: block;
          -webkit-user-drag: none;
        }
        .lexiq-splash-icon-wrap::after {
          content: "";
          position: absolute; inset: 0;
          pointer-events: none;
          /* Shine band tinted to the EXACT page background (#FAFAFA = 250,250,250)
             so when it sweeps over the empty bg it's invisible — only the scale
             icon (which has darker pixels) shows a moving highlight. */
          background: linear-gradient(115deg, transparent 42%, rgba(250,250,250,.92) 50%, transparent 58%);
          transform: translateX(-140%);
          mix-blend-mode: screen;
          animation: lexiqSplashShine 2.6s .9s linear infinite;
        }
        @keyframes lexiqSplashPop { to { opacity: 1; transform: translateY(0) scale(1); } }

        .lexiq-splash-logo {
          direction: ltr;
          unicode-bidi: isolate;
          font-family: 'Poppins', sans-serif;
          font-weight: 700;
          font-size: clamp(58px, 17vw, 108px);
          letter-spacing: clamp(0px, .3vw, 2px);
          display: inline-flex;
          align-items: baseline;
          line-height: 1;
          position: relative;
          max-width: 100%;
          white-space: nowrap;
        }
        .lexiq-splash-logo > span {
          display: inline-block;
          opacity: 0;
          transform: translateY(18px);
          filter: blur(6px);
        }
        .lexiq-splash-lex {
          color: #1B2128;
          animation: lexiqSplashReveal 1s .65s cubic-bezier(.22,1,.36,1) forwards;
        }
        .lexiq-splash-iq {
          font-weight: 800;
          /* Solid #22C55E — matches LexiqWordmark IQ color used on the
             home page (and everywhere else in the app). Removed the
             emerald gradient so the splash → home transition feels
             consistent rather than introducing a new green hue. */
          color: #22C55E;
          animation: lexiqSplashReveal 1s .85s cubic-bezier(.22,1,.36,1) forwards;
        }
        @keyframes lexiqSplashReveal {
          to { opacity: 1; transform: translateY(0); filter: blur(0); }
        }
        .lexiq-splash-logo::after {
          content: "";
          position: absolute; inset: -10% -8%;
          /* Same trick as the icon — match the page bg so the band is invisible
             over empty surface, and the letters do the reflecting. */
          background: linear-gradient(115deg, transparent 42%, rgba(250,250,250,.7) 50%, transparent 58%);
          transform: translateX(-140%);
          mix-blend-mode: screen;
          pointer-events: none;
          animation: lexiqSplashShine 3.8s 1.4s ease-in-out infinite;
        }
        @keyframes lexiqSplashShine {
          0%        { transform: translateX(-140%); }
          42%, 100% { transform: translateX(140%); }
        }

        .lexiq-splash-t1 {
          font-family: 'Amiri', serif;
          font-size: clamp(21px, 6.2vw, 30px);
          font-weight: 700;
          color: #1B2128;
          line-height: 2;
          max-width: 100%;
          margin-top: 30px;
          opacity: 0;
          animation: lexiqSplashFadeUp 1s 1.4s cubic-bezier(.22,1,.36,1) forwards;
        }
        .lexiq-splash-t2 {
          font-family: 'Amiri', serif;
          margin-top: 10px;
          font-size: clamp(17px, 5vw, 24px);
          font-weight: 400;
          color: #566069;
          line-height: 1.9;
          max-width: 100%;
          opacity: 0;
          animation: lexiqSplashFadeUp 1s 1.65s cubic-bezier(.22,1,.36,1) forwards;
        }
        .lexiq-splash-t2 b { color: #059669; font-weight: 700; }
        @keyframes lexiqSplashFadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        @media (prefers-reduced-motion: reduce) {
          .lexiq-splash-bub,
          .lexiq-splash-icon-wrap,
          .lexiq-splash-icon-wrap::after,
          .lexiq-splash-logo > span,
          .lexiq-splash-logo::after,
          .lexiq-splash-t1,
          .lexiq-splash-t2 { animation: none !important; }
          .lexiq-splash-bub { display: none; }
          .lexiq-splash-icon-wrap { opacity: 1 !important; transform: none !important; }
          .lexiq-splash-logo > span { opacity: 1 !important; transform: none !important; filter: none !important; }
          .lexiq-splash-t1, .lexiq-splash-t2 { opacity: 1 !important; }
        }
      `}</style>
    </div>
  );
}

/** Wrap top-level app routes. Renders the splash once per browser session,
    then the children. The underlying routes are NOT mounted while the splash
    is visible — that prevents other pages' useEffects from racing the splash
    over `theme-color` and over `body.background`.

    Additionally: the splash refuses to dismiss while the auth check is still
    in flight (UserAuthContext.loading=true). This guarantees that when the
    splash fades out, the next visible page is ALREADY the user's actual
    destination — Landing if logged in, /login otherwise — without any
    intermediate white / gray placeholder flash. */
/** Wrap top-level app routes. The splash is shown on EVERY app load (not
    just first-time), and only dismisses once BOTH:
      1. The splash's own timer / animation has elapsed, AND
      2. The auth check is no longer in-flight (UserAuthContext.loading=false)

    By holding the splash up until auth resolves, the next visible page
    is ALREADY the user's actual destination — Landing if logged in,
    /login otherwise — with zero intermediate white / gray placeholder.

    Returning users (who previously saw the splash this session) still see
    it briefly, but its visual duration is shortened (FAST_DISMISS_MS) so
    the perceived launch feels snappy. */
export function SplashGate({ children }) {
  const { loading: authLoading } = useUser();
  const seenBefore = (() => {
    if (typeof window === "undefined") return false;
    try { return sessionStorage.getItem(SHOWN_KEY) === "1"; } catch { return false; }
  })();
  const [splashFinishRequested, setSplashFinishRequested] = useState(false);

  const handleFinish = () => {
    try { sessionStorage.setItem(SHOWN_KEY, "1"); } catch (_e) { /* sessionStorage may be disabled */ }
    setSplashFinishRequested(true);
  };

  // `done` is purely a function of: splash animation finished AND
  // auth check is no longer in flight. Deriving it (rather than
  // setting in a useEffect) keeps the component rule-of-hooks clean
  // and avoids redundant re-renders.
  const done = splashFinishRequested && !authLoading;

  if (!done) return <SplashIntro onFinish={handleFinish} fast={seenBefore} />;
  return children;
}
